import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, ChartLine, Leaf, Plant, Sparkle } from "@phosphor-icons/react";
import { fetchPublicInsights, generateInsight } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1621452353638-888c49e1d340?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHx3aGVhdCUyMGZpZWxkJTIwYWVyaWFsfGVufDB8fHx8MTc3Njk4MDI0OXww&ixlib=rb-4.1.0&q=85";

export default function PublicInsights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchPublicInsights();
      setItems(res.items || []);
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const doc = await generateInsight();
      toast.success(`Ausgabe ${doc.slug} erstellt`);
      await load();
    } catch {
      toast.error("Fehler bei der Generierung");
    } finally {
      setGenerating(false);
    }
  };

  const hero = items[0];
  const rest = items.slice(1);

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="public-insights-root">
      <Helmet>
        <title>Weekly Insights · Bodenblick</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="description" content="Wöchentliche Lageberichte für Bodenblick-Pilotbetriebe." />
      </Helmet>
      <header className="border-b border-white/10 bg-[#121212]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft size={16} /> Zurück ins Cockpit
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="eid-label">Bodenblick</div>
              <div className="text-sm font-medium">Weekly Insights</div>
            </div>
            <button
              onClick={onGenerate}
              disabled={generating}
              data-testid="generate-insight-btn"
              className="flex items-center gap-2 bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 transition-colors rounded-sm px-3 py-2 text-sm"
            >
              <Sparkle size={14} /> {generating ? "Erzeuge…" : "Neuen Post erstellen"}
            </button>
          </div>
        </div>
      </header>

      <section className="relative">
        <div
          className="h-64 md:h-80 bg-cover bg-center"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(15,15,15,0.75), rgba(15,15,15,0.92)), url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto w-full px-6 pb-8 text-white">
            <div className="eid-label" style={{ color: "#C8E6C9" }}>
              Bodenblick · Aktuelles Lagebild aus Satellit, Wetter &amp; Markt
            </div>
            <h1 className="text-3xl md:text-6xl font-medium tracking-tight mt-2 max-w-3xl text-white" style={{ color: "white", lineHeight: 1.05 }}>
              Dein Boden.<br/>Dein Blick.<br/><span style={{ color: "#C8E6C9" }}>Aktuell.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base" style={{ color: "#D4D4D8" }}>
              Wir fusionieren Copernicus-Sentinel-Daten, europäische Wettermodelle und
              Agrar-Futures zu einem kurzen, anonymisierten Lagebericht — für Landwirte,
              Förster und alle, die wissen wollen, wie es den Feldern gerade geht.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {loading && (
          <div className="eid-card p-8 text-sm text-zinc-400" data-testid="insights-loading">
            Lade…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="eid-card p-8 text-center" data-testid="insights-empty">
            <Newspaper />
            <h2 className="text-xl font-medium mt-2">Noch keine Posts veröffentlicht</h2>
            <p className="text-sm text-zinc-400 mt-2">
              Klicke oben auf „Neuen Post erstellen", um den ersten Wochenrückblick zu generieren.
            </p>
          </div>
        )}

        {hero && (
          <article
            className="eid-card overflow-hidden grid grid-cols-1 md:grid-cols-5"
            data-testid="insight-hero"
          >
            <div
              className="md:col-span-2 min-h-[220px] bg-cover bg-center"
              style={{ backgroundImage: `linear-gradient(180deg, rgba(45,90,39,0.1), rgba(45,90,39,0.3)), url(${HERO_IMG})` }}
            />
            <div className="md:col-span-3 p-6 md:p-8 flex flex-col">
              <MetaRow slug={hero.slug} date={hero.created_at} />
              <h2 className="text-2xl md:text-3xl font-medium tracking-tight mt-3">
                {hero.title}
              </h2>
              <p className="text-zinc-400 mt-2">{hero.teaser}</p>
              <RegionChips regions={hero.regions} />
              <div className="mt-auto pt-4">
                <Link
                  to={`/insights/${hero.slug}`}
                  data-testid={`insight-read-${hero.slug}`}
                  className="inline-flex items-center gap-2 bg-[#F5F5F5] text-[#0A0A0A] hover:bg-[#333] transition-colors rounded-sm px-4 py-2 text-sm"
                >
                  Lesen <Leaf size={14} />
                </Link>
              </div>
            </div>
          </article>
        )}

        {rest.length > 0 && (
          <section>
            <h2 className="text-xl font-medium mb-3">Ältere Ausgaben</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="insight-grid">
              {rest.map((p) => (
                <article
                  key={p.slug}
                  className="eid-card p-5 flex flex-col gap-3 cursor-pointer eid-hover"
                  onClick={() => nav(`/insights/${p.slug}`)}
                  data-testid={`insight-card-${p.slug}`}
                >
                  <MetaRow slug={p.slug} date={p.created_at} />
                  <h3 className="text-lg font-medium tracking-tight">{p.title}</h3>
                  <p className="text-sm text-zinc-400 line-clamp-3">{p.teaser}</p>
                  <RegionChips regions={p.regions} compact />
                </article>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="border-t border-white/10 bg-[#121212] mt-10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row gap-3 justify-between text-xs text-zinc-500 font-mono">
          <span>Bodenblick · bodenblick.com · © {new Date().getFullYear()}</span>
          <span className="flex items-center gap-3">
            <a href="https://instagram.com/bodenblick" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="social-instagram">
              @bodenblick · Instagram
            </a>
          </span>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-6 text-[10px] text-zinc-500 font-mono">
          Daten: Copernicus CDSE · Open-Meteo · yfinance · Texte: Gemini 3 Flash
        </div>
      </footer>
    </div>
  );
}

function MetaRow({ slug, date }) {
  const d = (date || "").slice(0, 10);
  return (
    <div className="flex items-center gap-3 text-xs font-mono text-zinc-500">
      <span className="flex items-center gap-1"><Calendar size={12} /> {d || "–"}</span>
      <span className="uppercase">{slug}</span>
    </div>
  );
}

function RegionChips({ regions, compact }) {
  if (!regions || regions.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? "" : "mt-2"}`}>
      {regions.map((r, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10 px-2 py-0.5 rounded-sm"
        >
          <ChartLine size={10} /> {r.region_label} · {r.crop}
        </span>
      ))}
    </div>
  );
}

function Newspaper() {
  return <Plant size={28} className="inline-block text-[#00FA9A]" />;
}
